import { Injectable } from '@angular/core';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import {
  User,
  UpdateUser,
  ChangePassword,
  CreateUser,
  UpdateUserPassword,
  UpdateCurrentUser,
  DeleteUser,
} from '@model/user';
import { BaseService } from './base.service';

@Injectable({ providedIn: 'root' })
export class UserService extends BaseService {
  private _endpoint = 'user';

  public load$: Observable<Omit<User, 'password'>[]> = this._apiService
    .get$<User[]>(this._endpoint)
    .pipe(
      map(({ data }) => data),
      catchError((err) => throwError(() => err))
    );
  public loadCurrentUser$: Observable<Omit<User, 'password'>> = this._apiService
    .get$<User>(`${this._endpoint}/current-user`)
    .pipe(
      map(({ data }) => data),
      catchError((err) => throwError(() => err))
    );
  public add$ = (payload: CreateUser): Observable<Omit<User, 'password'>> =>
    this._apiService.post$<User>(this._endpoint, payload).pipe(
      tap(({ message }) => this._toast.success(message)),
      map(({ data }) => data),
      catchError((err) => this.handleError(err))
    );
  public edit$ = ({
    id,
    ...payload
  }: UpdateUser): Observable<Omit<User, 'password'>> =>
    this._apiService.put$<User>(`${this._endpoint}/id/${id}`, payload).pipe(
      tap(({ message }) => this._toast.success(message)),
      map(({ data }) => data),
      catchError((err) => this.handleError(err))
    );
  public editCurrentUser$ = (
    payload: UpdateCurrentUser
  ): Observable<Omit<User, 'password'>> =>
    this._apiService.put$<User>(this._endpoint, payload).pipe(
      tap(({ message }) => this._toast.success(message)),
      map(({ data }) => data),
      catchError((err) => this.handleError(err))
    );
  public editStatus$ = (id: string): Observable<Omit<User, 'password'>> =>
    this._apiService.patch$<User>(`${this._endpoint}/id/${id}/status`, {}).pipe(
      tap(({ message }) => this._toast.success(message)),
      map(({ data }) => data),
      catchError((err) => this.handleError(err))
    );
  public editUserPassword$ = ({
    id,
    ...payload
  }: UpdateUserPassword): Observable<Omit<User, 'password'>> =>
    this._apiService
      .patch$<User>(`${this._endpoint}/id/${id}/password`, payload)
      .pipe(
        tap(({ message }) => this._toast.success(message)),
        map(({ data }) => data),
        catchError((err) => this.handleError(err))
      );
  public editPassword$ = (
    params: ChangePassword
  ): Observable<Omit<User, 'password'>> =>
    this._apiService
      .patch$<User>(`${this._endpoint}/change-password`, params)
      .pipe(
        tap(({ message }) => this._toast.success(message)),
        map(({ data }) => data),
        catchError((err) => this.handleError(err))
      );
  public remove$ = ({ id }: DeleteUser): Observable<Omit<User, 'password'>> =>
    this._apiService.delete$<User>(`${this._endpoint}/id/${id}`).pipe(
      tap(({ message }) => this._toast.success(message)),
      map(({ data }) => data),
      catchError((err) => this.handleError(err))
    );
}
